import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from "@angular/core";
import { NgIf } from "@angular/common";
import { SharedModule } from "@shared/shared.module";
import { PremiumSummaryComponent } from "@shared/components/premium-summary/premium-summary.component";
import { FormBuilder, FormControl, FormGroup } from "@angular/forms";
import { ExtraCoverageComponent } from "@shared/components/coverages/extra-coverage/extra-coverage.component";
import { Select, Store } from "@ngxs/store";
import { Observable, Subject, pairwise, startWith, takeUntil } from "rxjs";
import { SatMotorState } from "@store/sat-motor/sat-motor.state";
import { COPY_PREMIUM_CALCULATION, DELETE_COVERAGE, DELETE_EXTRA_COVER_DETAILS, GET_NCD_ENQUIRY, STORE_NCD_INFO, UPDATE_COVERAGE_ALERT } from "@store/sat-motor/sat-motor.action";
import { VehicleNoValidator } from "@shared/classes/validators/vehicle-no.validator";
import { NcdMessageComponent } from "@shared/components/modal/ncd-message/ncd-message.component";
import { NxDialogService, NxModalRef } from "@aposin/ng-aquila/modal";
import { SimpleModalComponent } from "@shared/components/modal/simple-modal/simple-modal.component";
import { FormatDecimalPipe } from "@shared/pipes/format-decimal/format-decimal.pipe";
import { RemoveCommaPipe } from "@shared/pipes/remove-comma/remove-comma.pipe";
import { OkMessageComponent } from "@shared/components/modal/ok-message/ok-message.component";

@Component({
  selector: "app-coverage-third-party",
  standalone: true,
  imports: [NgIf, SharedModule, PremiumSummaryComponent, ExtraCoverageComponent],
  templateUrl: "./coverage-third-party.component.html",
  styleUrl: "./coverage-third-party.component.scss",
})
export class CoverageThirdPartyComponent implements OnInit {
  @ViewChild(ExtraCoverageComponent) extraCoverageComponentChild: ExtraCoverageComponent;
  @ViewChild('ncdBtn', { read: ElementRef }) ncdBtn: ElementRef;
  @Input() coverageFmGroup!: FormGroup;
  @Input() isThirdParty!: boolean;
  @Input() lov!: any;
  @Input() defaultDriver: any;
  @Input() issuanceInfo: any;
  @Input() partnerInfo: any;

  @Output() onCoverDeleted = new EventEmitter<unknown>();
  @Output() onCoverFormChanged = new EventEmitter<unknown>();
  @Output() alimCustomerEligible = new EventEmitter<unknown>();
  @Output() bettermentEligible = new EventEmitter<unknown>();

  @Select(SatMotorState.currentTypeOfCoverage) currentTypeOfCoverage$: Observable<SatMotorState>;
  @Select(SatMotorState.coverage) coverage$: Observable<SatMotorState>;
  @Select(SatMotorState.coveragePremium) coveragePremium$: Observable<SatMotorState>;
  @Select(SatMotorState.coverageShowAlert) coverageShowAlert$: Observable<SatMotorState>;

  private destroy$ = new Subject<void>();

  thirdPartyForm: FormGroup<any>;
  vehicleAgeLoadingList: any;
  driverAgeLoadingList: any;
  claimsExpLoadingList: any;
  vehicleAgeLoadingOldValue: any;
  driverAgeLoadingOldValue: any;
  claimsExpLoadingOldValue: any;
  vehicleNo: any;
  typeOfCoverage: any;
  isShowExtraCoverage: boolean = false;
  oldValue = {};
  decimalFmt: any;
  numberFmt: any;

  NCD_WO_NOTIFICATION = [
    "ENQ000", "ENQ014", "ENQ016", "ENQ017", "ENQ030", "ENQ044", "ENQ051", "ENQ057", "ENQ060", "ENQ062", 
    "ENQ063", "ENQ067", "ENQ078", "ENQ079", "ENQ080", "ENQ081", "ENQ088", "ENQ089", "ENQ092", "ENQ096"
  ];

  getNcdDisabled: boolean = true;

  ncdMessageModal?: NxModalRef<NcdMessageComponent>;
  warningMessageModal?: NxModalRef<SimpleModalComponent>;
  okMessageModal?: NxModalRef<OkMessageComponent>;

  coverageAlert: any;

  ALLRIDER_VEHICLE_USECODE = ["11", "12-02-01", "12", "12-03", "12-02-03"];

  constructor(
    private fb: FormBuilder,
    private store: Store,
    private readonly dialogService: NxDialogService,
  ) {
    this.decimalFmt = new FormatDecimalPipe();
    this.numberFmt = new RemoveCommaPipe();
    this.thirdPartyForm = this.fb.group({
      sumInsured: [{ value: 0, disabled: true }],
      annualPremium: [{ value: "", disabled: true }],
      grossPremium: [{ value: "", disabled: true }],
      basicPremium: [{ value: "", disabled: true }],
      allriderPrem: [{ value: "", disabled: true }],
      vehicleAgeLoading: "",
      driverAgeLoading: "",
      claimExperienceLoading: "",
      loadingAmount: [{ value: "", disabled: true }],
      ncdPercentage: [{ value: "", disabled: true }],
      ncdAmount: [{ value: "", disabled: true }],
      prevNcdInsurer: "",
      prevNcdVeh: ["", VehicleNoValidator()],
      loadingChanges: false
    });
  }

  ngOnInit() {
    this.issuanceInfo = this.store.selectSnapshot<any>((state) => state.SatMotorState.motor.step1.issuance.issuance);
    this.vehicleNo = this.issuanceInfo.vehicleNo;
    this.coverageAlert = this.store.selectSnapshot<any>((state) => state.SatMotorState.data.step3?.coverage?.coverageShowDialog);

    this.typeOfCoverage = this.store.selectSnapshot<any>((state) => state.SatMotorState.motor.step3?.coverage?.coverageType);
    if (this.typeOfCoverage && this.isThirdParty) { 

      this.thirdPartyForm.patchValue({
        sumInsured: this.decimalFmt.transform(this.typeOfCoverage["sumInsured"]),
        excessAmount: this.decimalFmt.transform(this.typeOfCoverage["excessAmount"]),
        annualPremium:this.decimalFmt.transform( this.typeOfCoverage["annualPremium"]),
        basicPremium: this.decimalFmt.transform(this.typeOfCoverage["basicPremium"]),
        allriderPrem: this.decimalFmt.transform(this.typeOfCoverage["allriderPrem"]),
        vehicleAgeLoading: this.typeOfCoverage["vehicleAgeLoading"],
        driverAgeLoading: this.typeOfCoverage["driverAgeLoading"],
        claimExperienceLoading: this.typeOfCoverage["claimExperienceLoading"],
        grossPremium: this.decimalFmt.transform(this.typeOfCoverage["grossPremium"]),
        ncdPercentage: this.decimalFmt.transform(this.typeOfCoverage["ncdPercentage"]),
        ncdAmount: this.decimalFmt.transform(this.typeOfCoverage["ncdAmount"]),
        prevNcdVeh: this.typeOfCoverage["prevNcdVeh"],
        prevNcdInsurer: this.typeOfCoverage["prevNcdInsurer"],
      }, { emitEvent: false });
      this.vehicleAgeLoadingOldValue = this.typeOfCoverage["vehicleAgeLoading"];
      this.driverAgeLoadingOldValue = this.typeOfCoverage["driverAgeLoading"];
      this.claimsExpLoadingOldValue = this.typeOfCoverage["claimExperienceLoading"];
    }

    this.coverage$.pipe(takeUntil(this.destroy$)).subscribe((state) => {
      if (state) {
        const forceDelete = state["forceDelete"];
        if (forceDelete) {
          const coverageType = state["coverageType"];
          if (coverageType && coverageType["coverCode"] === "20") {
            this.deleteCover(null);
          }
        }
      }
    });

    if (this.store.selectSnapshot<any>(state => state.SatMotorState?.isCancelReplaceCoverNote)) {
      this.getNcdDisabled = true;
    }
    else if (this.issuanceInfo.cnoteType === "NWTR" || this.store.selectSnapshot<any>((state) => state.SatMotorState.draftDetails)) {
      this.getNcdDisabled = false;
    } 

    this.currentTypeOfCoverage$.subscribe((state) => {
      if (state) {
        if (state["coverCode"] === "20") {
          this.coverageFmGroup.controls["typeOfCoverage"] = this.thirdPartyForm;
          this.typeOfCoverage = state;
          if (this.typeOfCoverage && this.isThirdParty) {
            let coverListLov = this.store.selectSnapshot<any>((state) => state.SatMotorState.data?.step3?.coverage?.coverListLov);
            for (let coverList of coverListLov) {
              if (coverList["coverCode"] === "20") {
                this.typeOfCoverage = { ...this.typeOfCoverage, coverDescription: coverList["coverDescription"] };
              }
            } 
          }
        }
      }
    });

    this.coverageShowAlert$.pipe(takeUntil(this.destroy$)).subscribe((state) => {
      if (state) {
        if (state["coverCode"] === "20") {
          const grossPremiumWarning = state["grossPremiumWarning"];
          const minPremAppliedWarning = state["minPremAppliedWarning"];
  
          if (state["bettermentEligible"] && !state["bettermentEligibleShowed"]) {
            this.bettermentEligible.emit();
          } 
          else if (state["alimCustomerEligible"] && !state["alimCustomerEligibleShowed"]) {
            this.alimCustomerEligible.emit();
          } 
          else if (grossPremiumWarning && !grossPremiumWarning["valid"]) {
            this.showGrossPremiumWarning(grossPremiumWarning["value"]);
          }
          else if (minPremAppliedWarning && !state["minPremAppliedWarningNotified"]) {
            this.showMinPremAppliedWarning(minPremAppliedWarning);
          }
        }
      }
    });

    this.coveragePremium$.pipe(takeUntil(this.destroy$)).subscribe((state) => {
      if (state) {

        if(!this.isThirdParty){
          return;
        }

        this.isShowExtraCoverage = false;

        const cngeNote = state["cngeNote"];
        if (cngeNote) {

          if (cngeNote.bettermentEligible) {
            this.coverageAlert = { ...this.coverageAlert, bettermentEligible: cngeNote.bettermentEligible };
          }

          const riskList = cngeNote["riskList"];
          if (riskList?.length) {

            for (let riskListItem of riskList) {
              this.prevNcdVeh.setValue(riskListItem.prevNcdVeh || riskListItem.vehicleNo);

              if (riskListItem.prevNcdInsurer) {
                this.prevNcdInsurer.setValue(riskListItem.prevNcdInsurer);
              } else if (this.typeOfCoverage && this.typeOfCoverage["prevNcdInsurer"]) {
                this.prevNcdInsurer.setValue(this.typeOfCoverage["prevNcdInsurer"]);
              }
              
              const coverList = riskListItem["coverList"];
              if (coverList?.length) {
                for (let coverListItem of coverList) {
                  if (this.isThirdParty && coverListItem["coverDto"]?.subCovList?.length > 0) {
                    this.isShowExtraCoverage = true;
                  }

                  this.coverageAlert = { ...this.coverageAlert, coverCode: coverListItem["coverCode"] };
                  
                  if (coverListItem["coverCode"] !== "20") return;

                  const grossPremium = coverListItem["grossPremium"];
                  const grossPremiumWarning = this.setGrossPremiumWarning(grossPremium);
                  this.coverageAlert = { ...this.coverageAlert, grossPremiumWarning: grossPremiumWarning };

                  const minPremApplied = cngeNote["minPremApplied"];
                  if (minPremApplied) {
                    this.coverageAlert = { ...this.coverageAlert, minPremAppliedWarning: minPremApplied, minPremAppliedWarningNotified: false };
                  }
                  
                  if (coverListItem.alimCustomerEligible) {
                    this.coverageAlert = { ...this.coverageAlert, alimCustomerEligible: coverListItem.alimCustomerEligible };
                  }

                  if(coverListItem["loadAmount"]){
                    this.thirdPartyForm.patchValue({
                      loadingAmount: coverListItem["loadAmount"].toFixed(2)
                    });
                  }

                  const coverDto = coverListItem["coverDto"];
                  if (coverDto) {
                    this.vehicleAgeLoadingList = coverDto["vehicleAgeLoadingList"];
                    this.driverAgeLoadingList = coverDto["driverAgeLoadingList"];
                    this.claimsExpLoadingList = coverDto["claimsExpLoadingList"];
                  }

                  const coverLoadingList = coverListItem["coverLoadingList"];
                  for (let coverLoadingItem of coverLoadingList) {
                    if (coverLoadingItem) {
                      if (coverLoadingItem["loadingCode"] === "VEHAGE" ) {
                        this.vehicleAgeLoadingOldValue = coverLoadingItem["loadingPct"];
                        this.thirdPartyForm.patchValue({
                          vehicleAgeLoading: this.vehicleAgeLoadingOldValue
                        }, { emitEvent: false });
                      } else if (coverLoadingItem["loadingCode"] === "DRVRAGE" ) {
                        this.driverAgeLoadingOldValue = coverLoadingItem["loadingPct"];
                        this.thirdPartyForm.patchValue({
                          driverAgeLoading: this.driverAgeLoadingOldValue
                        }, { emitEvent: false });
                      } else if (coverLoadingItem["loadingCode"] === "CLMEXP" ) {
                        this.claimsExpLoadingOldValue = coverLoadingItem["loadingPct"];
                        this.thirdPartyForm.patchValue({
                          claimExperienceLoading: this.claimsExpLoadingOldValue
                        }, { emitEvent: false });
                      }
                    }
                  }
                  
                  this.typeOfCoverage = {
                    ...this.typeOfCoverage,
                    ...{ grossPremium: grossPremium },
                    ...{ basicPremium: coverListItem["basicPremium"] },
                    ...{ ncdPercentage: coverListItem["ncdPercentage"] },
                    ...{ ncdAmount: coverListItem["ncdAmount"] },
                    ...{ subCoverPremium: coverListItem["subCoverPremium"] },
                    ...{ loadPercentage: coverListItem["loadPercentage"] },
                    ...{ loadAmount: coverListItem["loadAmount"] },
                  };
                }
              }
            }
          }
        }

        if (this.coverageAlert) {
          this.store.dispatch(new UPDATE_COVERAGE_ALERT(this.coverageAlert));
        }
      }
    });

  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onChangeForm($event: any) {

    let data = {
      fieldName: $event.target.getAttribute("formControlName"),
      value: $event.target.value,
      oldValue: this.oldValue[$event.target.getAttribute("formControlName")],
    };

    const doChangeForm = {
      prevNcdVeh: () => {
        if (this.store.selectSnapshot<any>(state => state.SatMotorState?.isCancelReplaceCoverNote)) {
          this.getNcdDisabled = true;
        } else if (this.issuanceInfo.cnoteType === "NWTR") {
          this.getNcdDisabled = false;
        } else if (["NWNN", "NWOO", "RN"].includes(this.issuanceInfo.cnoteType)) {
          if (this.prevNcdVeh.valid && this.prevNcdVeh.value && this.prevNcdVeh.value != 'NA') {
            this.getNcdDisabled = false;
          }
        }
      },
    };

    doChangeForm[data.fieldName]();

  }

  deleteCover(cover: any) {
    this.store.dispatch(new UPDATE_COVERAGE_ALERT(null)).subscribe((state) => {
      this.coverageAlert = null;
    });

    this.store.dispatch(new COPY_PREMIUM_CALCULATION());
    this.store.dispatch(new DELETE_COVERAGE(cover)).subscribe((state) => {
      this.thirdPartyForm.reset();
      this.onCoverDeleted.emit();
    });

    this.store.dispatch(new DELETE_EXTRA_COVER_DETAILS(cover)).subscribe((state) => {});

    this.typeOfCoverage = null;
    this.isShowExtraCoverage = false;
    
    this.getNcdDisabled = true;
  }

  isPrivateCars() {
    return ["080100", "080200", "080300"].includes(this.issuanceInfo.productCode);
  }

  isMotorcycle() {
    return ["082100", "082101"].includes(this.issuanceInfo.productCode);
  }

  setGrossPremiumWarning(grossPremium : any) {
    let value : any;
    if (this.isPrivateCars() && grossPremium < 50) {
      value = "50";
    }
    if (this.isMotorcycle() && grossPremium < 20) {
      value = "20";
    }
    
    if (value) {
      return { valid: false, value: value };
    }
    return { valid: true };
  }  

  showGrossPremiumWarning(value : any) {
    if (value) {
      this.dialogService.open(SimpleModalComponent, {
        showCloseIcon: false, width: "400px", maxWidth: "400px",
        disableClose: true,
        data: { type: "Warning", message: "Minimum Gross Premium Applied." },
      })
      .afterClosed().subscribe((result) => {
        this.typeOfCoverage = { ...this.typeOfCoverage, ...{ grossPremium: this.decimalFmt.transform(value) } };
        this.onCoverFormChanged.emit(this.typeOfCoverage);
      });
    }
  }

  showMinPremAppliedWarning(value : any) {
    if (value) {
      this.dialogService.open(SimpleModalComponent, {
        showCloseIcon: false, width: "400px", maxWidth: "400px",
        disableClose: true,
        data: { type: "Warning", message: "Minimum Gross Premium Applied." },
      })
      .afterClosed().subscribe((result) => {
        const coverageAlert = this.store.selectSnapshot<any>((state) => state.SatMotorState.data.step3?.coverage?.coverageShowDialog)
        this.store.dispatch(new UPDATE_COVERAGE_ALERT({ ...coverageAlert, minPremAppliedWarningNotified: true }));
      });
    }
  }

  onNcdInsurerChange(event: any) {
    if (event.value === null) {
      this.prevNcdInsurer?.setValue('');
    }
  }

  onClickGetNcd() {
    if (this.getNcdDisabled)
      return;
    
    this.getNcdDisabled = !this.getNcdDisabled;

    this.getNcd();
  }

  getNcd() {
    let vehicleDetailsForm = this.coverageFmGroup.get("vehicleDetails");
    
    let vehicleClass;
    const selectedMakeModel = this.store.selectSnapshot<any>((state) => state.SatMotorState?.data?.step3.coverage?.selectedMakeModel);
    if (selectedMakeModel?.classCode) {
      vehicleClass = selectedMakeModel?.classCode;
    }

    let coverCode;
    if (this.typeOfCoverage) {
      coverCode = this.typeOfCoverage?.coverCode;
    }
    
    let payload = {
      cnoteType: this.issuanceInfo.cnoteType,
      cnoteDate: this.issuanceInfo.effectiveDate || this.issuanceInfo.cnoteDate,
      productType: this.issuanceInfo.productCode,
      idType: this.partnerInfo.idType1,
      idNo1: this.partnerInfo.idValue1,
      idNo2: this.partnerInfo.idValue2,
      vehicleUse: vehicleDetailsForm.get("vehicleUse").value,
      vehicleNo: vehicleDetailsForm.get("vehicleNo").value,
      vehicleClass: vehicleClass,
      chassisNo: vehicleDetailsForm.get("vehicleChassis").value,
      coverCode: coverCode,
      previousNcdInsurer: this.prevNcdInsurer.value,
      previousNcdVehicle: this.prevNcdVeh.value,
    };

    this.store.dispatch(new GET_NCD_ENQUIRY(payload)).subscribe((state) => {
      let ncdInfo = state.SatMotorState.ncdEnquiryDetails;
      if (ncdInfo) {
        this.store.dispatch(new STORE_NCD_INFO(ncdInfo));
        if (ncdInfo.ismResCode) {
          if (this.NCD_WO_NOTIFICATION.includes(ncdInfo.ismResCode)) {
            if (ncdInfo?.changeNcdToZero) {
              this.dialogService.open(OkMessageComponent, {
                disableClose: true,
                width: "400px", data: { message: ncdInfo.errMsg }
              }).afterClosed().subscribe((result) => {
                this.thirdPartyForm.patchValue({
                  ncdPercentage: this.decimalFmt.transform(ncdInfo.currNcdPct),
                });
                this.onCoverFormChanged.emit();
              });
            } else {
              this.thirdPartyForm.patchValue({
                ncdPercentage: this.decimalFmt.transform(ncdInfo.currNcdPct),
              });
              this.onCoverFormChanged.emit();
            }
          }
          else {
            this.dialogService.open(OkMessageComponent, {
              disableClose: true,
              width: "400px", data: { message: ncdInfo.errMsg }
            }).afterClosed().subscribe((result) => {
              this.thirdPartyForm.patchValue({
                ncdPercentage: this.decimalFmt.transform(ncdInfo.currNcdPct),
              });
              this.onCoverFormChanged.emit();
            });
          }
        } else {
          this.dialogService.open(OkMessageComponent, {
            disableClose: true,
            width: "400px", data: { message: ncdInfo.errMsg }
          }).afterClosed().subscribe((result) => {
            this.thirdPartyForm.patchValue({
              ncdPercentage: this.decimalFmt.transform(0),
            });
            this.onCoverFormChanged.emit();
          });
        }
      } else {
        this.dialogService.open(OkMessageComponent, {
          disableClose: true,
          width: "400px", data: { message: "NCD Not Allowed = 0%. Unable to connect to ISM NCD System. Please retry or contact System Administrator." }
        }).afterClosed().subscribe((result) => {
          this.thirdPartyForm.patchValue({
            ncdPercentage: this.decimalFmt.transform(0),
          });
          this.onCoverFormChanged.emit();
        });
      }
    });
  }

  onVehicleAgeLoadingChanges($event) {
    if (this.vehicleAgeLoadingOldValue !== $event.value) {
      this.vehicleAgeLoadingOldValue = $event.value;
      this.thirdPartyForm.patchValue({ loadingChanges: true });
      this.onCoverFormChanged.emit();
    }
  };

  onDriverAgeLoadingChanges($event) {
    if (this.driverAgeLoadingOldValue !== $event.value) {
      this.driverAgeLoadingOldValue = $event.value;
      this.thirdPartyForm.patchValue({ loadingChanges: true });
      this.onCoverFormChanged.emit();
    }
  };

  onClaimExperienceLoadingChanges($event) {
    if (this.claimsExpLoadingOldValue !== $event.value) {
      this.claimsExpLoadingOldValue = $event.value;
      this.thirdPartyForm.patchValue({ loadingChanges: true }); 
      this.onCoverFormChanged.emit();
    }
  };

  get prevNcdVeh() {
    return this.thirdPartyForm.controls["prevNcdVeh"];
  }

  get prevNcdInsurer() {
    return this.thirdPartyForm.controls["prevNcdInsurer"];
  }

  get vehicleAgeLoading() {
    return this.thirdPartyForm.controls["vehicleAgeLoading"];
  }

  get driverAgeLoading() {
    return this.thirdPartyForm.controls["driverAgeLoading"];
  }

  get claimExperienceLoading() {
    return this.thirdPartyForm.controls["claimExperienceLoading"];
  }

  getNcdBtn() : ElementRef {
    return this.ncdBtn;
  }

  setNcdBtn(disabled: boolean) {
    this.ncdBtn.nativeElement.disabled = disabled;
    this.getNcdDisabled = disabled;
  }

  callThirdPartyComponent(methodName: any) {
    if(methodName === 'callExtraCoverageCalculatePremium'){
      return this.extraCoverageComponentChild.calculatePremium(false);
    }else{
      return this.extraCoverageComponentChild.motorRoadWarriorValidation();
    }
  }

  get screenWidth() {
    return window.innerWidth >= 704;
  }

  showAllRiderPremium() {
    const vehicleUseCode = this.store.selectSnapshot<any>((state) => state.SatMotorState.motor.step3?.coverage?.vehicleDetails?.vehicleUse);

    if (!this.isThirdParty || !vehicleUseCode) return false;

    const isAllRiderVehicleUseValid = this.ALLRIDER_VEHICLE_USECODE.includes(vehicleUseCode);
    
    return this.isMotorcycle() && this.isThirdParty && isAllRiderVehicleUseValid
  }
  
}
